// Gestionnaires d'événements pour les flip cards
import {CardFace} from '../types/flipcardTypes';
import {I18nStrings} from '../i18n';
import {updateCardHeader, updateFooterButton} from './content';
import {
  flipCardContainerClass,
  flipCardContentClass,
  flipCardContentOptionsClass,
  flipCardFaceClass,
  flipCardResetBtnClass
} from "@/components/tinyMce/plugins/flipcard/styles";
import {
  flipcardCardContentTypeAttr,
  flipcardCardFaceAttr,
  flipcardCardIdAttr, flipcardCardNumberAttr
} from "@/components/tinyMce/plugins/flipcard/flipcardConstants";

export class ContentHandlers {
  private readonly editor: any;
  private readonly i18n: I18nStrings;
  private readonly onInsertImage: (cardId: string, face: CardFace) => void;
  private readonly onDeleteCard: (cardId: string) => void;

  constructor(editor: any, i18n: I18nStrings, onInsertImage: (cardId: string, face: CardFace) => void,
              onDeleteCard: (cardId: string) => void) {
    this.editor = editor;
    this.i18n = i18n;
    this.onInsertImage = onInsertImage;
    this.onDeleteCard = onDeleteCard;
  }

  public rebindEventListenersToAllCards() {
    const cards = this.editor.dom.select(flipCardContainerClass);
    cards.forEach((card: HTMLElement) => {
      card.dataset.eventsAttached = 'true';
      const activeFace = card.querySelector('.flipcard-face.active') as HTMLElement;
      const content = activeFace?.querySelector('.flipcard-content') as HTMLElement;
      const textContent: HTMLElement = content?.querySelector('.flipcard-text-content') as HTMLElement;
      if (textContent) {
        textContent.dataset.placeholder = this.i18n.addTextPlaceholder;
        this.applyTextContentClass(textContent);
      }
      const hasContent = content?.classList.contains('has-content');

      if (hasContent) {
        const resetBtn = card.querySelector('.flipcard-btn-reset') as HTMLElement;
        if (resetBtn) {
          resetBtn.style.display = 'flex';
        }
      }
      const clone = card.cloneNode(true) as HTMLElement;
      card.replaceWith(clone);
      setTimeout(() => this.attachEventListeners(clone), 100);
    });
  }

  private applyTextContentClass(textContent: HTMLElement) {
    if (!!textContent.textContent?.trim().length) {
      textContent.classList.add('has-text');
    } else {
      textContent.innerHTML = '\u200B';
      textContent.dataset.hasZeroWidthText = 'true';
    }
  }

  public attachEventListeners(container: HTMLElement): void {
    // Header actions
    this.attachHeaderActions(container);

    // Content options
    this.attachContentOptions(container);

    // Flip button
    this.attachFlipButton(container);

    this.attachTextContentListeners(container);
  }

  private attachHeaderActions(container: HTMLElement): void {
    const copyBtn = container.querySelector('.flipcard-btn-copy');
    const deleteBtn = container.querySelector('.flipcard-btn-delete');
    const resetBtn = container.querySelector(flipCardResetBtnClass);

    if (copyBtn) {
      copyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleCopy(container);
      });
    }

    if (deleteBtn) {
      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleDelete(container);
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleReset(container);
      });
    }
  }

  private attachContentOptions(container: HTMLElement): void {
    const textButtons = container.querySelectorAll('.flipcard-option-text');
    const imageButtons = container.querySelectorAll('.flipcard-option-image');

    textButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const face = (btn.closest(flipCardFaceClass) as HTMLElement)?.getAttribute(flipcardCardFaceAttr) as CardFace;
        this.handleAddText(container, face);
      });
    });

    imageButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation()
        const face = (btn.closest(flipCardFaceClass) as HTMLElement)?.getAttribute(flipcardCardFaceAttr) as CardFace;
        this.handleAddImage(container, face);
      });
    });
  }

  private attachFlipButton(container: HTMLElement): void {
    const flipBtn = container.querySelector('.flipcard-btn-flip');
    if (flipBtn) {
      flipBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleFlip(container);
      });
    }
  }

  private attachTextContentListeners(container: HTMLElement): void {
    const textDivs = container.querySelectorAll('.flipcard-text-content[contenteditable="true"]');
    textDivs.forEach((textDiv: Element) => {
      const textDivClone = textDiv.cloneNode(true) as HTMLElement;
      setTimeout(() => {
        const handleInput = () => {
          this.applyTextContentClass(textDivClone);
        };

        const handleBlur = () => {
          if (textDivClone.dataset.hasZeroWidthText) {
            delete textDivClone.dataset.hasZeroWidthText;
            textDivClone.innerHTML = '';
            textDivClone.classList.remove('has-text');
          }
        };

        textDivClone.addEventListener('input', handleInput);
        textDivClone.addEventListener('blur', handleBlur);

        textDiv.replaceWith(textDivClone);
      }, 100);
    });
  }

  private handleCopy(container: HTMLElement): void {
    const cardId = container.getAttribute(flipcardCardIdAttr);
    if (cardId) {
      this.editor.execCommand('flipcardCopy', false, cardId);
    }
  }

  private handleDelete(container: HTMLElement): void {
    const cardId = container.getAttribute(flipcardCardIdAttr);
    if (cardId) {
      this.onDeleteCard(cardId);
    }
  }

  private handleReset(container: HTMLElement): void {
    const activeFace = container.querySelector(`${flipCardFaceClass}.active`) as HTMLElement;
    const face = activeFace?.getAttribute(flipcardCardFaceAttr) as CardFace;
    const cardId = container.getAttribute(flipcardCardIdAttr);

    if (cardId && face) {
      this.editor.execCommand('flipcardReset', false, {cardId, face});
    }
  }

  private handleAddText(container: HTMLElement, face: CardFace): void {
    const faceElement = container.querySelector(`.flipcard-${face}`) as HTMLElement;
    if (!faceElement) return;

    const options = faceElement.querySelector(flipCardContentOptionsClass) as HTMLElement;
    const content = faceElement.querySelector(flipCardContentClass) as HTMLElement;

    if (options && content) {
      options.classList.add('hidden');
      content.classList.add('has-content');
      content.setAttribute(flipcardCardContentTypeAttr, 'text');

      content.innerHTML = `<div contenteditable="true" class="flipcard-text-content" data-placeholder="${this.i18n.addTextPlaceholder}"></div>`;

      this.attachTextContentListeners(container);

      // Focus initial
      const textDiv = content.querySelector('[contenteditable="true"]') as HTMLElement;
      if (textDiv) {
        setTimeout(() => {
          textDiv.focus();
          const range = document.createRange();
          const sel = window.getSelection();
          range.setStart(textDiv, 0);
          range.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }, 200);
      }

      this.showResetButton(container);
    }
  }

  private handleAddImage(container: HTMLElement, face: CardFace): void {
    const cardId = container.getAttribute(flipcardCardIdAttr);
    if (cardId) {
      // Forcer la perte de focus de la card
      this.editor.selection.collapse(true);
      setTimeout(() => {
        this.onInsertImage(cardId, face);
      }, 200);
    }
  }

  public insertImage(container: HTMLElement, face: CardFace, imageUrl: string): void {
    const faceElement = container.querySelector(`.flipcard-${face}`) as HTMLElement;
    if (!faceElement) return;

    const options = faceElement.querySelector(flipCardContentOptionsClass) as HTMLElement;
    const content = faceElement.querySelector(flipCardContentClass) as HTMLElement;

    if (options && content) {
      // Masquer les options
      options.classList.add('hidden');

      // Afficher le contenu avec image
      content.classList.add('has-content');
      content.setAttribute(flipcardCardContentTypeAttr, 'image');
      content.innerHTML = `<img src="${imageUrl}"/>`;

      // Afficher le bouton Reset
      this.showResetButton(container);
    }
  }

  public resetFace(container: HTMLElement, face: CardFace): void {
    const faceElement = container.querySelector(`.flipcard-${face}`) as HTMLElement;
    if (!faceElement) return;

    const options = faceElement.querySelector(flipCardContentOptionsClass) as HTMLElement;
    const content = faceElement.querySelector(flipCardContentClass) as HTMLElement;

    if (options && content) {
      // Réafficher les options
      options.classList.remove('hidden');

      // Masquer et vider le contenu
      content.classList.remove('has-content');
      content.setAttribute(flipcardCardContentTypeAttr, 'empty');
      content.innerHTML = '';

      // Masquer le bouton Reset si les deux faces sont vides
      this.updateResetButtonVisibility(container);
    }
  }

  private handleFlip(container: HTMLElement): void {
    const faces = container.querySelectorAll(flipCardFaceClass);
    const currentActive = container.querySelector(`${flipCardFaceClass}.active`) as HTMLElement;

    faces.forEach((face) => face.classList.remove('active'));

    const newFace = currentActive?.getAttribute(flipcardCardFaceAttr) === 'front' ? 'back' : 'front';
    const newActive = container.querySelector(`.flipcard-${newFace}`) as HTMLElement;
    if (newActive) {
      newActive.classList.add('active');

      // Mettre à jour le header et footer
      const cardNumber = parseInt(container.getAttribute(flipcardCardNumberAttr) || '1', 10);
      updateCardHeader(container, cardNumber, newFace as CardFace, this.i18n);
      updateFooterButton(container, newFace as CardFace, this.i18n);

      // Mettre à jour la visibilité du bouton Reset
      this.updateResetButtonVisibility(container);
    }
  }

  public updateCardNumbers(): void {
    const cards = this.editor.dom.select('.flipcard-container');
    cards.forEach((card: HTMLElement, index: number) => {
      const cardNumber = index + 1;
      card.setAttribute(flipcardCardNumberAttr, cardNumber.toString());

      const activeFace = card.querySelector(`${flipCardFaceClass}.active`) as HTMLElement;
      const face = activeFace?.getAttribute(flipcardCardFaceAttr) as CardFace || 'front';

      updateCardHeader(card, cardNumber, face, this.i18n);
    });
  }

  private showResetButton(container: HTMLElement): void {
    const resetBtn = container.querySelector(flipCardResetBtnClass) as HTMLElement;
    if (resetBtn) {
      resetBtn.style.display = 'flex';
    }
  }

  private updateResetButtonVisibility(container: HTMLElement): void {
    const activeFace = container.querySelector(`${flipCardFaceClass}.active`) as HTMLElement;
    const content = activeFace?.querySelector(flipCardContentClass) as HTMLElement;
    const hasContent = content?.classList.contains('has-content');

    const resetBtn = container.querySelector(flipCardResetBtnClass) as HTMLElement;
    if (resetBtn) {
      if (hasContent) {
        resetBtn.style.display = 'flex';
      } else {
        resetBtn.style.display = 'none';
      }
    }
  }
}
